import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getCurrentCoordinates } from '../utils/geolocation';

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface MapLocationPickerProps {
  open: boolean;
  onClose: () => void;
  onLocationSelect: (latitude: number, longitude: number, address?: string) => void;
  initialLatitude?: number;
  initialLongitude?: number;
  initialAddress?: string;
}

interface LocationMarkerProps {
  position: [number, number] | null;
  setPosition: (position: [number, number]) => void;
}

const LocationMarker: React.FC<LocationMarkerProps> = ({ position, setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  return position === null ? null : (
    <Marker position={position} />
  );
};

export const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  open,
  onClose,
  onLocationSelect,
  initialLatitude,
  initialLongitude,
  initialAddress,
}) => {
  const { t } = useTranslation();
  const [position, setPosition] = useState<[number, number] | null>(
    initialLatitude && initialLongitude ? [initialLatitude, initialLongitude] : null
  );
  const [address, setAddress] = useState(initialAddress || '');
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);
  const [geocodingError, setGeocodingError] = useState<string>('');

  // Default to Dubai coordinates if no initial position
  const defaultCenter: [number, number] = [25.2048, 55.2708];
  const mapCenter = position || defaultCenter;

  const handleConfirm = () => {
    if (position) {
      onLocationSelect(position[0], position[1], address);
      onClose();
    }
  };

  const handleAddressSearch = async () => {
    if (!address.trim()) return;

    setIsGeocodingLoading(true);
    setGeocodingError('');

    try {
      // Using Nominatim (OpenStreetMap) for geocoding - free service
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        setPosition([lat, lon]);
        setAddress(data[0].display_name || address);
      } else {
        setGeocodingError(t('address_not_found'));
      }
    } catch (error) {
      setGeocodingError(t('geocoding_error'));
    } finally {
      setIsGeocodingLoading(false);
    }
  };

  const handleCurrentLocation = async () => {
    setGeocodingError('');
    try {
      const { latitude, longitude } = await getCurrentCoordinates();
      setPosition([latitude, longitude]);
    } catch (error: any) {
      console.error('Error getting current location:', error);
      if (error?.message === 'geolocation_not_supported') {
        setGeocodingError(t('geolocation_not_supported'));
      } else {
        setGeocodingError(t('location_access_denied'));
      }
    }
  };

  const handleReverseGeocode = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();
      if (data && data.display_name) {
        setAddress(data.display_name);
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error);
    }
  };

  useEffect(() => {
    if (position && !address) {
      handleReverseGeocode(position[0], position[1]);
    }
  }, [position, address]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('select_location_on_map')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label={t('search_address')}
            variant="outlined"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddressSearch()}
            InputProps={{
              endAdornment: (
                <Button
                  onClick={handleAddressSearch}
                  disabled={isGeocodingLoading}
                  sx={{ minWidth: 'auto' }}
                >
                  {isGeocodingLoading ? <CircularProgress size={20} /> : t('search')}
                </Button>
              ),
            }}
            sx={{ mb: 1 }}
          />
          <Button
            variant="outlined"
            onClick={handleCurrentLocation}
            sx={{ mr: 1 }}
          >
            {t('use_current_location')}
          </Button>
        </Box>

        {geocodingError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {geocodingError}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('click_map_to_select_location')}
        </Typography>

        <Box sx={{ height: 400, width: '100%', border: '1px solid #ccc', borderRadius: 1 }}>
          <MapContainer
            center={mapCenter}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <LocationMarker position={position} setPosition={setPosition} />
          </MapContainer>
        </Box>

        {position && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('selected_coordinates')}: {position[0].toFixed(6)}, {position[1].toFixed(6)}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          disabled={!position}
        >
          {t('confirm_location')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
